import React, { useMemo } from 'react';
import { Table, Typography } from 'antd';
import { AreaLink } from '../AreaLink';
import { MetricLink } from '../MetricLink';
import { resolveFieldRange, resolveWarningRangesForMetric } from '../../helpers/metricRules';

export interface TableMetricStat {
    average?: number | null;
    min?: number | null;
    max?: number | null;
    count?: number | null;
}

export interface TableRow {
    id: string;
    label: string;
    metricKey: string;
    metricName: string;
    areaId?: number;
    stats: TableMetricStat;
}

// New interface for Report component usage
interface CommonSectionProps {
    section: any;
    data: any[];
    areas: any[];
    areaTypes: any[];
    metrics: any[];
    previewAreaId: number;
    onAreaClick?: (areaId: number) => void;
    onMetricClick?: (metricKey: string, metricId: number | undefined, areaId: number) => void;
}

// Legacy interface for backward compatibility
interface LegacyProps {
    title?: string;
    rows: TableRow[];
}

type Props = CommonSectionProps | LegacyProps;

// Type guard to check if props are CommonSectionProps
function isCommonSectionProps(props: Props): props is CommonSectionProps {
    return 'section' in props && 'data' in props;
}

const formatNumber = (value?: number | null) => {
    if (value === undefined || value === null || Number.isNaN(value)) return '—';
    return Number(value).toFixed(2);
};

const DEFAULT_PALETTE = ['#1677ff', '#2f54eb', '#52c41a', '#faad14', '#ff4d4f'];

const toRgba = (hexColor: string, alpha: number) => {
    const hex = hexColor.replace('#', '').trim();
    if (hex.length === 3) {
        const r = parseInt(hex[0] + hex[0], 16);
        const g = parseInt(hex[1] + hex[1], 16);
        const b = parseInt(hex[2] + hex[2], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgba(22, 119, 255, ${alpha})`;
};

const rangeKeyForField = (field: string) => {
    if (field === 'average') return 'avg';
    if (field === 'stddev') return 'stddev';
    return field;
};

export const ReportTable: React.FC<Props> = (props) => {
    const { Text } = Typography;

    // Transform data and build columns if using CommonSectionProps
    const { title, rows, columns, rowStyle, areaLabel } = useMemo(() => {
        if (isCommonSectionProps(props)) {
            // New format: transform data from section config
            const { section, data, areas } = props;
            
            // Build area name lookup
            const areaNameMap = new Map<number, string>();
            const areaById = new Map<number, any>();
            areas.forEach((a: any) => {
                if (a.id && a.name) {
                    areaNameMap.set(a.id, a.name);
                }
                if (a?.id) {
                    areaById.set(a.id, a);
                }
            });
            
            // Build metric name lookup
            const metricNameMap = new Map<string, string>();
            props.metrics.forEach((m: any) => {
                if (m.key && m.name) {
                    metricNameMap.set(m.key, m.name);
                }
            });

            const metricConfigByKey = new Map<string, any>();
            props.metrics.forEach((m: any) => {
                if (m?.key) metricConfigByKey.set(m.key, m);
            });
            
            // Transform raw API data into table rows
            let transformedRows: TableRow[] = [];
            
            if (Array.isArray(data) && data.length > 0) {
                if (section.aggregateChildren) {
                    // Aggregate children - group by BOTH groupBy dimension AND metricKey
                    // This respects the groupBy setting while still aggregating multiple measurements
                    const groupByKey = section.groupBy || 'childArea';
                    const byGroup = new Map<string, any[]>();
                    
                    data.forEach((d: any) => {
                        const metricKey = d.metricKey || 'metric';
                        // Create composite key based on groupBy type
                        let groupIdentifier: string;
                        if (groupByKey === 'childArea' || groupByKey === 'area') {
                            groupIdentifier = String(d.groupId || d.areaId || 'unknown');
                        } else if (groupByKey === 'pattern') {
                            groupIdentifier = String(d.groupId || 'unknown');
                        } else if (groupByKey === 'patternPoint') {
                            groupIdentifier = String(d.groupId || 'unknown');
                        } else {
                            groupIdentifier = String(d.groupId || 'unknown');
                        }
                        
                        const compositeKey = `${groupIdentifier}___${metricKey}`;
                        if (!byGroup.has(compositeKey)) byGroup.set(compositeKey, []);
                        byGroup.get(compositeKey)!.push(d);
                    });
                    
                    transformedRows = Array.from(byGroup.entries()).map(([compositeKey, items]) => {
                        const [groupIdentifier, metricKey] = compositeKey.split('___');
                        const firstItem = items[0];
                        // Aggregate stats from all children
                        const avgValues: number[] = [];
                        const minValues: number[] = [];
                        const maxValues: number[] = [];
                        let totalCount = 0;
                        let totalSum = 0;
                        const medianValues: number[] = [];
                        const varianceValues: number[] = [];
                        const stddevValues: number[] = [];
                        
                        items.forEach((item: any) => {
                            const rawStats = item.stats || {};
                            const avg = rawStats.average ?? rawStats.avg ?? item.average ?? item.avg;
                            const min = rawStats.min ?? item.min;
                            const max = rawStats.max ?? item.max;
                            const count = rawStats.count ?? item.count;
                            const sum = rawStats.sum ?? item.sum;
                            const median = rawStats.median ?? item.median;
                            const variance = rawStats.variance ?? item.variance;
                            const stddev = rawStats.stddev ?? item.stddev;
                            
                            if (avg !== null && avg !== undefined) avgValues.push(Number(avg));
                            if (min !== null && min !== undefined) minValues.push(Number(min));
                            if (max !== null && max !== undefined) maxValues.push(Number(max));
                            if (count !== null && count !== undefined) totalCount += Number(count);
                            if (sum !== null && sum !== undefined) totalSum += Number(sum);
                            if (median !== null && median !== undefined) medianValues.push(Number(median));
                            if (variance !== null && variance !== undefined) varianceValues.push(Number(variance));
                            if (stddev !== null && stddev !== undefined) stddevValues.push(Number(stddev));
                        });
                        
                        // Determine label based on groupBy type
                        let label: string;
                        const groupByKey = section.groupBy || 'childArea';
                        
                        if (groupByKey === 'childArea' || groupByKey === 'area') {
                            // Use area name from map
                            const areaId = Number(groupIdentifier);
                            label = areaNameMap.get(areaId) || firstItem.groupLabel || `Area ${groupIdentifier}`;
                        } else if (groupByKey === 'pattern') {
                            label = firstItem.groupLabel || `Pattern ${groupIdentifier}`;
                        } else if (groupByKey === 'patternPoint') {
                            label = firstItem.groupLabel || `Point ${groupIdentifier}`;
                        } else {
                            label = firstItem.groupLabel || String(groupIdentifier);
                        }
                        
                        return {
                            id: `aggregated-${groupIdentifier}-${metricKey}`,
                            label,
                            metricKey,
                            metricName: metricNameMap.get(metricKey) || metricKey,
                            areaId: groupByKey === 'childArea' || groupByKey === 'area'
                                ? Number(groupIdentifier)
                                : undefined,
                            stats: {
                                average: avgValues.length > 0 ? avgValues.reduce((a, b) => a + b, 0) / avgValues.length : null,
                                min: minValues.length > 0 ? Math.min(...minValues) : null,
                                max: maxValues.length > 0 ? Math.max(...maxValues) : null,
                                count: totalCount || null,
                                sum: totalSum || null,
                                median: medianValues.length > 0 ? medianValues.reduce((a, b) => a + b, 0) / medianValues.length : null,
                                variance: varianceValues.length > 0 ? varianceValues.reduce((a, b) => a + b, 0) / varianceValues.length : null,
                                stddev: stddevValues.length > 0 ? stddevValues.reduce((a, b) => a + b, 0) / stddevValues.length : null,
                            },
                        };
                    });
                } else {
                    // Show individual child rows
                    transformedRows = data.map((d: any, idx: number) => {
                        // Get area name if groupId or areaId is a number
                        let label = d.groupLabel;
                        if (!label && typeof d.groupId === 'number') {
                            label = areaNameMap.get(d.groupId) || `Area ${d.groupId}`;
                        } else if (!label && typeof d.areaId === 'number') {
                            label = areaNameMap.get(d.areaId) || `Area ${d.areaId}`;
                        } else if (!label) {
                            label = String(d.groupId || d.areaId || `Group ${idx + 1}`);
                        }
                        
                        // Normalize stats - backend may return 'avg' instead of 'average'
                        const rawStats = d.stats || {};
                        const normalizedStats = {
                            average: rawStats.average ?? rawStats.avg ?? d.average ?? d.avg,
                            avg: rawStats.avg ?? rawStats.average ?? d.avg ?? d.average,
                            min: rawStats.min ?? d.min,
                            max: rawStats.max ?? d.max,
                            count: rawStats.count ?? d.count,
                            sum: rawStats.sum ?? d.sum,
                            median: rawStats.median ?? d.median,
                            variance: rawStats.variance ?? d.variance,
                            stddev: rawStats.stddev ?? d.stddev,
                        };
                        
                        const metricKey = d.metricKey || 'metric';
                        const resolvedAreaId = typeof d.areaId === 'number'
                            ? d.areaId
                            : typeof d.groupId === 'number'
                                ? d.groupId
                                : undefined;
                        return {
                            id: `row-${d.groupId ?? d.areaId ?? idx}-${metricKey}-${idx}`,
                            label,
                            metricKey,
                            metricName: metricNameMap.get(metricKey) || metricKey,
                            areaId: resolvedAreaId,
                            stats: normalizedStats,
                        };
                    });
                }
            }
            
            // Build dynamic columns based on section.fields configuration
            const groupByType = section.groupBy || 'childArea';
            const isAreaGrouping = groupByType === 'area' || groupByType === 'childArea';
            
            const baseColumns = [
                { 
                    title: 'Group', 
                    dataIndex: 'label', 
                    key: 'label',
                    render: (text: string, record: any) => {
                        // Check if this is an area grouping and we have onAreaClick
                        if (isAreaGrouping && props.onAreaClick && typeof record.areaId === 'number') {
                            if (!isNaN(record.areaId) && record.areaId > 0) {
                                return <AreaLink areaId={record.areaId} areaName={text} onClick={props.onAreaClick} />;
                            }
                        }
                        return text;
                    },
                },
                { 
                    title: 'Metric', 
                    dataIndex: 'metricName', 
                    key: 'metricName',
                    render: (text: string, record: any) => {
                        // Make metric names clickable with onMetricClick
                        if (props.onMetricClick) {
                            const targetAreaId = typeof record.areaId === 'number' && !isNaN(record.areaId)
                                ? record.areaId
                                : props.previewAreaId;
                            
                            return (
                                <MetricLink
                                    metricKey={record.metricKey}
                                    metricName={text}
                                    metricId={props.metrics.find(m => m.key === record.metricKey)?.id}
                                    areaId={targetAreaId}
                                    onClick={props.onMetricClick}
                                />
                            );
                        }
                        return text;
                    },
                },
            ];
            
            // Field labels map
            const fieldLabels: Record<string, string> = {
                average: 'Average',
                avg: 'Avg',
                min: 'Min',
                max: 'Max',
                count: 'Count',
                sum: 'Sum',
                median: 'Median',
                variance: 'Variance',
                stddev: 'Std Dev',
            };
            
            // If fields are configured, only show those columns
            const configuredFields = Array.isArray(section.fields) && section.fields.length > 0
                ? section.fields
                : ['average', 'min', 'max', 'count']; // default fields
            
            const statColumns = configuredFields.map((field: string) => {
                // Normalize 'avg' to 'average' for display
                const normalizedField = field === 'avg' ? 'average' : field;
                return {
                    title: fieldLabels[normalizedField] || normalizedField.charAt(0).toUpperCase() + normalizedField.slice(1),
                    dataIndex: ['stats', normalizedField],
                    key: normalizedField,
                    render: (val: any, record: any) => {
                        if (normalizedField === 'count') {
                            return val ?? '—';
                        }
                        const metricConfig = metricConfigByKey.get(record.metricKey);
                        const targetArea = typeof record.areaId === 'number'
                            ? areaById.get(record.areaId)
                            : areaById.get(props.previewAreaId);
                        const ranges = resolveWarningRangesForMetric(metricConfig, targetArea);
                        const rangeKey = rangeKeyForField(normalizedField);
                        const range = resolveFieldRange(ranges, rangeKey) ?? ranges?.[normalizedField];
                        const min = range?.min ?? null;
                        const max = range?.max ?? null;
                        const isLow = min !== null && typeof val === 'number' && val < min;
                        const isHigh = max !== null && typeof val === 'number' && val > max;
                        const isWarning = isLow || isHigh;
                        const formatted = formatNumber(val);
                        return (
                            <span
                                className={isWarning ? 'report-warning-value' : undefined}
                                style={isWarning ? { color: '#ff4d4f', fontWeight: 600 } : undefined}
                            >
                                {formatted}
                            </span>
                        );
                    },
                };
            });

            const rowStyle = (record: any) => {
                const paletteRaw = metricConfigByKey.get(record.metricKey)?.colorPalette;
                const palette = paletteRaw && paletteRaw.length > 0 ? paletteRaw : DEFAULT_PALETTE;
                const accentColor = palette.find((color: string) => color) || DEFAULT_PALETTE[0];
                return { backgroundColor: toRgba(accentColor, 0.08) };
            };
            
            return {
                title: section.title,
                rows: transformedRows,
                columns: [...baseColumns, ...statColumns],
                rowStyle,
                areaLabel:
                    areas.find((area: any) => area?.id === props.previewAreaId)?.name ||
                    undefined,
            };
        } else {
            // Legacy format: use props directly
            const legacyColumns = [
                { title: 'Group', dataIndex: 'label', key: 'label' },
                { title: 'Metric', dataIndex: 'metricKey', key: 'metricKey' },
                { title: 'Avg', dataIndex: ['stats', 'average'], key: 'avg', render: formatNumber },
                { title: 'Min', dataIndex: ['stats', 'min'], key: 'min', render: formatNumber },
                { title: 'Max', dataIndex: ['stats', 'max'], key: 'max', render: formatNumber },
                { title: 'Count', dataIndex: ['stats', 'count'], key: 'count', render: (val: any) => val ?? '—' },
            ];
            
            return {
                title: props.title,
                rows: props.rows,
                columns: legacyColumns,
                rowStyle: undefined,
                areaLabel: undefined,
            };
        }
    }, [props]);

    if (!rows.length) {
        return (
            <div className="report-table-empty">
                <Text type="secondary">No data</Text>
            </div>
        );
    }

    return (
        <div className="report-table-surface">
            {title || areaLabel ? (
                <div className="report-table-head">
                    <div className="report-table-head-main">
                        {title ? <div className="report-table-title">{title}</div> : null}
                        <div className="report-table-meta">
                            <span>{rows.length} rows</span>
                            {areaLabel ? <span>{areaLabel}</span> : null}
                        </div>
                    </div>
                </div>
            ) : null}
            <div className="report-table-frame">
                <Table
                    className="report-table-grid"
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={rows}
                    columns={columns}
                    scroll={{ x: 'max-content' }}
                    onRow={rowStyle ? (record) => ({ style: rowStyle(record) }) : undefined}
                />
            </div>
        </div>
    );
};

export default ReportTable;
